import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { EmptyBranchNote } from "@/components/EmptyBranchNote";
import { Button } from "@/components/ui/button";
import { formatPhone } from "@/lib/phone";
import { deliverFile } from "@/lib/pdfFiling";

const KINDS = [
  { key: "", label: "Everything" },
  { key: "tax_invoice", label: "Tax invoices" },
  { key: "gold_bullion", label: "Bullion" },
  { key: "buying_contract", label: "Buying contracts" },
  { key: "loan", label: "Loans" },
  { key: "repair", label: "Repairs" },
  { key: "credit", label: "Holding credit" },
];

const BUCKETS = [
  { key: "", label: "All ages" },
  { key: "0-7", label: "0–7 days" },
  { key: "8-30", label: "8–30 days" },
  { key: "31-60", label: "31–60 days" },
  { key: "60+", label: "60+ days" },
];

export default function MoneyOwed() {
  const { branch } = useAuth();
  const [kind, setKind] = useState("");
  const [bucket, setBucket] = useState("");
  const [allBranches, setAllBranches] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useCallback(
    () => ({
      ...(kind ? { kind } : {}),
      ...(bucket ? { bucket } : {}),
      ...(allBranches ? {} : { branch }),
    }),
    [kind, bucket, allBranches, branch]
  );

  const load = useCallback(() => {
    setLoading(true);
    setData(null);
    const url = kind === "credit" ? "/reports/credit-held" : "/reports/outstanding";
    const query = kind === "credit" ? { ...(allBranches ? {} : { branch }) } : params();
    api
      .get(url, { params: query })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load what's owed"))
      .finally(() => setLoading(false));
  }, [params, kind, allBranches, branch]);

  useEffect(() => {
    load();
  }, [load]);

  const remind = async (row) => {
    if (row.kind === "repair") {
      try {
        const { data } = await api.post(`/repairs/${row.id}/notify`);
        toast.success(`Reminder sent to ${data.sent_to}`);
      } catch (err) {
        toast.error(
          (err.response?.data?.detail && String(err.response.data.detail)) ||
            "Could not send that reminder"
        );
      }
      return;
    }
    try {
      const { data } = await api.post(`/documents/${row.id}/email`);
      toast.success(`Invoice emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not email that record"
      );
    }
  };

  const downloadCsv = async () => {
    const credit = kind === "credit";
    try {
      const res = await api.get(
        credit ? "/reports/credit-held.csv" : "/reports/outstanding.csv",
        {
          params: credit ? { ...(allBranches ? {} : { branch }) } : params(),
          responseType: "blob",
        }
      );
      await deliverFile(
        res.data,
        "Reports",
        credit ? "credit-held.csv" : "money-owed.csv",
        toast
      );
    } catch (e) {
      toast.error("Could not download the CSV");
    }
  };

  const t = data?.totals;
  const showingCredit = kind === "credit";

  return (
    <div className="fade-up" data-testid="money-owed">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        {allBranches ? "All branches" : branch}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Money Owed</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        {showingCredit
          ? "The other side of the ledger — deposits sitting on customer accounts that no invoice has claimed yet. This is money the shop is holding, so use it on their next invoice."
          : "Every invoice, contract, loan and repair that is not paid in full — oldest first, so nothing slips through. Loans show principal plus interest worked out to today."}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="money-owed-filters">
        {KINDS.map((k) => (
          <button
            key={k.key || "all"}
            type="button"
            onClick={() => setKind(k.key)}
            className={`border px-4 py-2 text-sm transition-colors duration-150 ${
              kind === k.key
                ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                : "border-border hover:bg-muted"
            }`}
            data-testid={`money-owed-kind-${k.key || "all"}`}
          >
            {k.label}
          </button>
        ))}
        <label className="flex items-center gap-2 px-2 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            data-testid="money-owed-all-branches"
          />
          Both stores
        </label>
        <Button
          variant="outline"
          className="ml-auto rounded-none"
          onClick={downloadCsv}
          data-testid="money-owed-csv-button"
        >
          <Download className="mr-1.5 h-4 w-4" /> CSV
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {!showingCredit &&
          BUCKETS.map((b) => (
          <button
            key={b.key || "all"}
            type="button"
            onClick={() => setBucket(b.key)}
            className={`border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors duration-150 ${
              bucket === b.key
                ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                : "border-border hover:bg-muted"
            }`}
            data-testid={`money-owed-bucket-${b.key || "all"}`}
          >
            {b.label}
            {t?.ageing?.[b.key] ? ` · ${money(t.ageing[b.key])}` : ""}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {(showingCredit
          ? [
              ["Credit held", money(t?.credit ?? 0), "credit-held-total"],
              ["Customers", String(t?.customers ?? 0), "credit-held-customers"],
              ["Deposits", String(t?.count ?? 0), "credit-held-count"],
              ["Longest held", `${t?.oldest_days ?? 0} days`, "credit-held-oldest"],
            ]
          : [
              ["Total outstanding", money(t?.outstanding ?? 0), "money-owed-total"],
              ["Records owing", String(t?.count ?? 0), "money-owed-count"],
              ["Already paid", money(t?.paid ?? 0), "money-owed-paid"],
              ["Oldest", `${t?.oldest_days ?? 0} days`, "money-owed-oldest"],
            ]
        ).map(([label, value, testid]) => (
          <div key={testid} className="border border-border p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-2 text-2xl font-bold" data-testid={testid}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-border">
        {showingCredit ? (
          <table className="w-full min-w-[860px] text-sm" data-testid="credit-held-table">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                {["Date taken", "Customer", "Phone", "Store", "Method", "Credit held",
                  "Days held", "Note"].map((h) => (
                  <th
                    key={h}
                    className={`px-3 py-2.5 font-semibold ${
                      ["Credit held", "Days held"].includes(h) ? "text-right" : "text-left"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(!data?.rows || data.rows.length === 0) && (
                <tr>
                  <td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                    {loading ? "Loading…" : "No credit being held — every deposit has been used."}
                    {!loading && !allBranches && !bucket && (
                      <EmptyBranchNote className="mx-auto mt-4 max-w-xl" />
                    )}
                  </td>
                </tr>
              )}
              {(data?.rows || []).map((r) => (
                <tr
                  key={r.entry_id || `${r.phone}-${r.date}-${r.amount}`}
                  className="hover:bg-[#FBF8F2]"
                  data-testid={`credit-held-row-${r.entry_id}`}
                >
                  <td className="tabular px-3 py-2.5">{r.date}</td>
                  <td className="px-3 py-2.5 font-semibold">
                    <Link to={`/customers/${r.phone}`} className="hover:underline">
                      {r.customer_name || "No name"}
                    </Link>
                  </td>
                  <td className="tabular px-3 py-2.5">
                    {r.phone ? formatPhone(r.phone) : "-"}
                  </td>
                  <td className="px-3 py-2.5 text-xs uppercase tracking-wider text-muted-foreground">
                    {r.branch}
                  </td>
                  <td className="px-3 py-2.5 text-xs uppercase tracking-wider">{r.method}</td>
                  <td className="tabular px-3 py-2.5 text-right font-bold text-[#166534]">
                    {money(r.amount)}
                  </td>
                  <td className="tabular px-3 py-2.5 text-right">{r.days}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
        <table className="w-full min-w-[980px] text-sm" data-testid="money-owed-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {["Date", "Reference", "Type", "Customer", "Phone", "Total", "Paid",
                "Outstanding", "Days", ""].map((h, i) => (
                <th
                  key={i}
                  className={`px-3 py-2.5 font-semibold ${
                    ["Total", "Paid", "Outstanding", "Days"].includes(h)
                      ? "text-right"
                      : "text-left"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan={10} className="px-3 py-12 text-center text-muted-foreground">
                  {loading ? "Loading…" : "Nothing owing — everything is paid up."}
                  {!loading && !allBranches && !kind && !bucket && (
                    <EmptyBranchNote className="mx-auto mt-4 max-w-xl" />
                  )}
                </td>
              </tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr key={`${r.kind}-${r.id}`} className="hover:bg-[#FBF8F2]" data-testid={`money-owed-row-${r.reference}`}>
                <td className="tabular px-3 py-2.5">{r.date}</td>
                <td className="px-3 py-2.5 font-semibold">
                  <Link
                    to={r.kind === "repair" ? `/repairs/${r.id}` : `/documents/${r.id}`}
                    className="hover:underline"
                  >
                    {r.kind === "repair" ? r.reference : `#${r.reference}`}
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-xs uppercase tracking-wider text-muted-foreground">
                  {(r.kind || "").replace("_", " ")}
                  {r.payment_method?.startsWith("KUBER") && (
                    <span
                      className="mt-1 block w-fit border border-[#C5A059] bg-[#FBF8F2] px-1.5 py-0.5 text-[10px] font-semibold normal-case text-[#7A5C1E]"
                      data-testid={`money-owed-awaiting-kuber-${r.reference}`}
                    >
                      Awaiting Kuber
                    </span>
                  )}
                  {r.due_date && (
                    <span className="block normal-case text-[11px]">due {r.due_date}</span>
                  )}
                </td>
                <td className="px-3 py-2.5">{r.customer_name}</td>
                <td className="tabular px-3 py-2.5">{r.customer_phone ? formatPhone(r.customer_phone) : "-"}</td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.total)}</td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.paid)}</td>
                <td className="tabular px-3 py-2.5 text-right font-bold text-[#9A3412]">
                  {money(r.outstanding)}
                </td>
                <td className="tabular px-3 py-2.5 text-right">{r.days}</td>
                <td className="px-3 py-2.5 text-right">
                  {r.customer_email ? (
                    <button
                      type="button"
                      onClick={() => remind(r)}
                      className="text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                      data-testid={`money-owed-remind-${r.reference}`}
                      aria-label="Email a reminder"
                    >
                      <Mail className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">no email</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
